import { Button, Flex, Link, Text, useBreakpoint } from '@chakra-ui/react';
import { Form, FormikContext, FormikHelpers, useFormik } from 'formik';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { COUNTRY_CONFIG } from '../../constants/countries';
import { LDFlags } from '../../constants/flags';
import { stateAbbreviations, statesArray } from '../../constants/states';
import {
  ERROR_TYPES,
  OLD_CAR_ERROR_MSG,
  UNABLE_TO_ASSIST_ERROR_MSG,
} from '../../constants/tempInfos';
import { ACCOUNT_LOGIN } from '../../constants/urls';
import {
  CreateTemporaryInfoMutation,
  useCreateReferralLazyQuery,
  useCreateTemporaryInfoMutation,
} from '../../gql/generated/graphql';
import { useFlag, useFlowCountry, useStep } from '../../hooks';
import { CookieKeys, clearCookie, useCookie } from '../../hooks/useCookie';
import useStartAsSeller from '../../hooks/useStartAsSeller';
import { dashboardDealInfoAtom } from '../../pages/dashboard/RootDashboard';
import {
  allowDuplicateVinAtom,
  devVinAtom,
} from '../../pages/dashboard/components/devToolbarAtoms';
import {
  FlowParams,
  PrequalParams,
  ReferralParams,
  getExtraInfo,
} from '../../pages/flowEntryPages/utils';
import { trackFlowStart } from '../../services/analytics/conversionEvents';
import { RudderEvent, rudderanalytics } from '../../services/analytics/rudderstack';
import { stateValidation } from '../../utils/validation/address';
import validateLicenseNumber, {
  INVALID_LICENSE_PLATE_MSG,
  INVALID_VIN_MSG,
} from '../../utils/validation/licenseNumberValidator';
import validateVin from '../../utils/validation/vinValidator';
import DuplicateDealMessage from '../DuplicateDealMessage';
import Loader from '../Loader';
import Subtitle from '../Subtitle';
import Switch from '../Switch';
import Input from '../formComponents/Input';
import Select from '../formComponents/Select';
import VehicleManualEntryModal from './VehicleManualEntryModal';

export const DECODING_SERVICE_ISSUE_MSG =
  'Our decoding service is experiencing issues. Please enter your info manually';

export const licensePlateValidationSchema = Yup.object({
  licensePlate: Yup.string()
    .required('Required field')
    .test('is-valid-license-number', INVALID_LICENSE_PLATE_MSG, async (value) =>
      validateLicenseNumber({ licenseNumber: value }),
    ),
  state: stateValidation,
});

export const vinValidationSchema = Yup.object({
  vin: Yup.string()
    .required('Required field')
    .test('is-valid-vin', INVALID_VIN_MSG, (value) => validateVin(value)),
});

export const LICENSE_PLATE_NAME = 'PLATE';
export const VIN_NAME = 'VIN';

type FormFields = Yup.InferType<typeof licensePlateValidationSchema & typeof vinValidationSchema>;

type LicensePlateOrVinInputProps = {
  autoFocus?: boolean;
  flowParams?: FlowParams;
  referral?: ReferralParams;
  onReady?: () => void;
};

const LicensePlateOrVinInput = ({
  autoFocus = false,
  flowParams,
  referral,
  onReady,
}: LicensePlateOrVinInputProps) => {
  const comReferralProgram = useFlag(LDFlags.COM_REFERRAL_PROGRAM);
  const country = useFlowCountry();
  const { plateLookup } = COUNTRY_CONFIG[country];
  const [, setGuid] = useCookie<string>(CookieKeys.GUID_KEY);
  const [, setReferralCode] = useCookie<string>(CookieKeys.REFERRAL_CODE);
  const [, setReferralId] = useCookie<string>(CookieKeys.REFERRAL_ID);
  const [, setReferrerFn] = useCookie<string>(CookieKeys.REFERRER_FN);
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const [prequalInfo] = useCookie<PrequalParams>(CookieKeys.PREQUAL_PARAMS);
  const setDashboardDealInfo = useSetAtom(dashboardDealInfoAtom);
  const isAuthenticated = !!accessToken;
  const [currentInput, setCurrentInput] = useState(
    !plateLookup || flowParams?.vin ? VIN_NAME : LICENSE_PLATE_NAME,
  );
  const { pathname, search } = useLocation();
  const wantsToSell = useStartAsSeller();

  const device = useBreakpoint();
  const step = useStep();
  const ldClient = useLDClient();

  const [dealExists, setDealExists] = useState(false);
  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = useState(false);
  const [flowStartError, setFlowStartError] = useState('');
  const isLicensePlate = LICENSE_PLATE_NAME === currentInput;
  const [createTempInfo] = useCreateTemporaryInfoMutation({
    context: {
      isErrorHandled: true,
    },
  });
  const [createReferral] = useCreateReferralLazyQuery({
    context: {
      isErrorHandled: true,
    },
  });
  const [allowDuplicateVin, setAllowDuplicateVin] = useAtom(allowDuplicateVinAtom);
  const devVin = useAtomValue(devVinAtom);
  const hasData =
    !!flowParams?.vin || (!!flowParams?.licensePlate && !!flowParams?.licensePlateState);

  const handleSubmit = async (values: FormFields, { setFieldError }: FormikHelpers<FormFields>) => {
    const vin = values.vin?.toUpperCase();
    const licensePlate = values.licensePlate?.replace(/\s/g, '')?.toUpperCase();
    const state = values.state?.toUpperCase();

    const isVin = !!vin;

    if (!isVin && (!licensePlate || !stateAbbreviations.includes(state))) {
      return;
    }

    if (isVin && !validateVin(vin)) {
      setFieldError('vin', INVALID_VIN_MSG);
      return;
    }
    if (!isVin && !validateLicenseNumber({ licenseNumber: licensePlate })) {
      setFieldError('licensePlate', INVALID_LICENSE_PLATE_MSG);
      return;
    }

    clearCookie(CookieKeys.QUERY_PARAMS);

    // workaround to fix apollo-client issue:
    // https://github.com/apollographql/apollo-client/issues/9354#issuecomment-1234952933
    const tempInfoMutation: CreateTemporaryInfoMutation = await new Promise((resolve, reject) =>
      createTempInfo({
        variables: {
          vin,
          license_plate_number: licensePlate,
          license_plate_state: state,
          device_type: device,
          extra_input: getExtraInfo(country, flowParams, prequalInfo, wantsToSell),
          query_params: search,
          ignore_existing_deal: allowDuplicateVin,
        },
        onCompleted: resolve,
        onError: (error) => {
          const extensions = error.graphQLErrors[0]?.extensions;
          const dealId = extensions?.dealId;
          const dealState = extensions?.dealState;

          rudderanalytics.track(RudderEvent.Error, {
            error_message: error.message,
            vin,
            license_plate: licensePlate,
            license_plate_state: state,
            pathname,
            date: new Date().toISOString(),
            deal_id: dealId,
            deal_state: dealState,
          });

          if (error.message === ERROR_TYPES.DEAL_EXISTS) {
            setDealExists(true);
          }
          if (error.message === ERROR_TYPES.OLD_CAR) {
            setFlowStartError(OLD_CAR_ERROR_MSG);
          }
          if (error.message === ERROR_TYPES.RESTRICTED_CAR) {
            setFlowStartError(UNABLE_TO_ASSIST_ERROR_MSG);
          }
          if (error.message === ERROR_TYPES.DECODING_SERVICE_ISSUE) {
            setFieldError('vin', DECODING_SERVICE_ISSUE_MSG);
          }
          if (error.message === ERROR_TYPES.INVALID_LICENSE_PLATE) {
            setFieldError('licensePlate', INVALID_LICENSE_PLATE_MSG);
          }
          reject();
        },
      }),
    );

    const id = tempInfoMutation?.createTemporaryInfo?.id as string;
    const tempInfoData = tempInfoMutation?.createTemporaryInfo?.data;

    if (referral?.referrer_id && referral.referrer_id > 0 && comReferralProgram) {
      try {
        await createReferral({
          variables: {
            referrer_id: referral.referrer_id ?? -1,
            temporary_info_id: id,
          },
        });
      } catch {
        // non-blocking: continue the flow
      }
    }

    trackFlowStart(ldClient, id, search, tempInfoData);

    setGuid(id);
    if (comReferralProgram) {
      setReferralCode(flowParams?.referralCode || '');
      setReferralId(String(flowParams?.referrerId || 0));
      setReferrerFn(flowParams?.referrerFn || '');
    }
    setDashboardDealInfo(undefined);
    setAllowDuplicateVin(false);
    clearCookie(CookieKeys.PREQUAL_PARAMS);
    step.moveNext({}, true);
  };

  const formik = useFormik<FormFields>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      licensePlate: flowParams?.licensePlate || '',
      state: flowParams?.licensePlateState || '',
      vin: flowParams?.vin || '',
    },
    validationSchema: isLicensePlate ? licensePlateValidationSchema : vinValidationSchema,
    onSubmit: handleSubmit,
  });

  const { isSubmitting, setErrors, setValues, errors } = formik;

  const isInitialized = useRef(false);

  const handleWarningsReset = () => {
    setDealExists(false);
    setFlowStartError('');
  };

  const handleOnSwitchChange = (newValue: string): void => {
    setCurrentInput(newValue);
    setValues({ vin: '', licensePlate: '', state: '' });
    setErrors({});
  };

  useEffect(() => {
    const formSubmit = async () => {
      try {
        await formik.submitForm();
      } catch (error) {
        // Error is already handled in handleSubmit's onError callback
      } finally {
        onReady?.();
      }
    };

    if (!isInitialized.current) {
      isInitialized.current = true;
      if (hasData) {
        formSubmit();
      } else {
        onReady?.();
      }
    }
  }, [flowParams, hasData]);

  useEffect(() => {
    if (allowDuplicateVin) {
      handleWarningsReset();
    }
  }, [allowDuplicateVin]);

  useEffect(() => {
    if (devVin) {
      handleOnSwitchChange(VIN_NAME);
      setValues({ vin: devVin, licensePlate: '', state: '' });
      handleWarningsReset();
    }
  }, [devVin]);

  if (!isInitialized.current) {
    return null;
  }

  const isDecodingError = errors.vin === DECODING_SERVICE_ISSUE_MSG;

  if (hasData && isSubmitting) {
    return <Loader />;
  }

  return (
    <>
      <FormikContext.Provider value={formik}>
        <Form style={{ width: '100%' }}>
          <Flex
            bg={{ base: '#ffffffcc', md: 'white' }}
            border="1px solid"
            borderColor="gray.200"
            shadow="2xl"
            mx="auto"
            maxW="370px"
            rounded="10px"
            p="4"
            flexDirection="column"
          >
            <Flex alignItems="center" flexDirection={{ sm: 'column', md: 'row' }}>
              <Text
                noOfLines={2}
                mr={{ md: '3' }}
                textAlign={{ base: 'center', md: 'left' }}
                mt={1}
                mb={{ sm: '6', md: '0' }}
              >
                GET STARTED WITH YOUR {isLicensePlate ? 'LICENSE PLATE' : `VEHICLE'S VIN`}
              </Text>
              {plateLookup && (
                <Flex h="40px" ml={{ md: '1' }} minW="170px">
                  <Switch
                    firstOption={LICENSE_PLATE_NAME}
                    secondOption={VIN_NAME}
                    onChange={(val) => {
                      handleOnSwitchChange(val);
                      handleWarningsReset();
                    }}
                    selectedOption={currentInput}
                  />
                </Flex>
              )}
            </Flex>
            <Flex mt="4" mb={1} minH="50px">
              {isLicensePlate ? (
                <>
                  <Input
                    name="licensePlate"
                    placeholder="License Plate"
                    pr="5px"
                    autoFocus={autoFocus}
                    textTransform="uppercase"
                    onChange={handleWarningsReset}
                    invalid={dealExists || !!flowStartError}
                  />
                  <Flex maxW={{ sm: '110px', md: '100%' }}>
                    <Select
                      name="state"
                      placeholder="State"
                      options={statesArray}
                      onChange={handleWarningsReset}
                      invalid={dealExists}
                    />
                  </Flex>
                </>
              ) : (
                <Input
                  name="vin"
                  placeholder="VIN"
                  textTransform="uppercase"
                  onChange={handleWarningsReset}
                  invalid={dealExists || !!flowStartError}
                />
              )}
            </Flex>
            {dealExists && <DuplicateDealMessage />}
            {flowStartError && (
              <Text
                as="span"
                color="red.500"
                fontWeight="semibold"
                textAlign="center"
                wordBreak="break-word"
              >
                {flowStartError}
              </Text>
            )}
            {!dealExists && !flowStartError && (
              <Flex direction="column" alignItems="center">
                {isDecodingError ? (
                  <Button
                    w="100%"
                    py="20px"
                    fontWeight="600"
                    onClick={() => setIsManualEntryModalOpen(true)}
                  >
                    ENTER VEHICLE INFO MANUALLY
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    w="100%"
                    py="20px"
                    fontWeight="600"
                    isLoading={isSubmitting}
                  >
                    GET STARTED
                  </Button>
                )}
              </Flex>
            )}
            {!isAuthenticated && !dealExists && !flowStartError && (
              <Subtitle
                textAlign="center"
                lineHeight="25px"
                fontSize="14px"
                maxW={{ base: '350px', lg: '750px' }}
                margin="10px"
                fontWeight="bold"
              >
                Already have an account?{' '}
                <Link
                  as={ReactRouterLink}
                  to={ACCOUNT_LOGIN}
                  color="royalBlue"
                  fontSize="14px"
                  letterSpacing={0}
                  fontWeight="medium"
                >
                  Login now
                </Link>
              </Subtitle>
            )}
          </Flex>
        </Form>
      </FormikContext.Provider>
      <VehicleManualEntryModal
        isOpen={isManualEntryModalOpen}
        setIsOpen={setIsManualEntryModalOpen}
      />
    </>
  );
};

export default LicensePlateOrVinInput;
